import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress, Alert, Card, CardContent,
    Grid, Avatar, Tooltip, useTheme, useMediaQuery, TablePagination
} from '@mui/material';
import {
    Store, Person, Visibility, CheckCircle, Pending, Close, Refresh,
    Phone, Email, Business, AccountBalanceWallet, Edit
} from '@mui/icons-material';
import adminService from '../services/adminService';

const PendingSellers = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [loading, setLoading] = useState(true);
    const [sellers, setSellers] = useState([]);
    const [error, setError] = useState(null);
    const [selectedSeller, setSelectedSeller] = useState(null);
    const [detailsDialog, setDetailsDialog] = useState(false);
    const [approveDialog, setApproveDialog] = useState(false);
    const [rejectDialog, setRejectDialog] = useState(false);
    const [processing, setProcessing] = useState(false);

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const loadPendingSellers = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await adminService.getSellers();
            const pendingSellers = response.sellers.filter(seller => 
                seller.sellerStatus === 'pending' || seller.sellerStatus === 'new'
            );
            setSellers(pendingSellers);
        } catch (err) {
            console.error('Failed to load pending sellers:', err);
            setError('Failed to load pending sellers');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPendingSellers();
    }, [loadPendingSellers]);

    const handleViewDetails = (seller) => {
        setSelectedSeller(seller);
        setDetailsDialog(true);
    };

    const handleApproveSeller = async () => {
        if (!selectedSeller) return;
        
        setProcessing(true);
        try {
            await adminService.updateSellerStatus(selectedSeller._id, 'verified');
            setSellers(sellers.filter(s => s._id !== selectedSeller._id));
            setApproveDialog(false);
            setDetailsDialog(false);
            setSelectedSeller(null);
        } catch (err) {
            console.error('Failed to approve seller:', err);
            setError('Failed to approve seller');
        } finally {
            setProcessing(false);
        }
    };

    const handleRejectSeller = async () => {
        if (!selectedSeller) return;
        
        setProcessing(true);
        try {
            await adminService.updateSellerStatus(selectedSeller._id, 'rejected');
            setSellers(sellers.filter(s => s._id !== selectedSeller._id));
            setRejectDialog(false);
            setDetailsDialog(false);
            setSelectedSeller(null);
        } catch (err) {
            console.error('Failed to reject seller:', err);
            setError('Failed to reject seller');
        } finally {
            setProcessing(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Pending sx={{ fontSize: 32, color: '#FF9800' }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Pending Seller Approvals
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Review and approve seller verification requests
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip label={`${sellers.length} Pending`} color="warning" sx={{ fontWeight: 600 }} />
                    <IconButton onClick={loadPendingSellers}>
                        <Refresh />
                    </IconButton>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#fff3e0' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Pending sx={{ fontSize: 40, color: '#FF9800' }} />
                            <Typography variant="h4" fontWeight={700}>{sellers.length}</Typography>
                            <Typography variant="body2" color="text.secondary">Pending Approval</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#e8f5e9' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <CheckCircle sx={{ fontSize: 40, color: '#4CAF50' }} />
                            <Typography variant="h4" fontWeight={700}>0</Typography>
                            <Typography variant="body2" color="text.secondary">Approved Today</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#ffebee' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Close sx={{ fontSize: 40, color: '#F44336' }} />
                            <Typography variant="h4" fontWeight={700}>0</Typography>
                            <Typography variant="body2" color="text.secondary">Rejected Today</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#e3f2fd' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Store sx={{ fontSize: 40, color: '#2196F3' }} />
                            <Typography variant="h4" fontWeight={700}>
                                {sellers.reduce((sum, s) => sum + (s.stats?.totalProducts || 0), 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Total Products</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Pending Sellers Table */}
            {sellers.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <CheckCircle sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No pending seller approvals
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        All sellers have been reviewed
                    </Typography>
                </Paper>
            ) : (
                <TableContainer 
                    component={Paper}
                    sx={{ 
                        maxHeight: { xs: '400px', sm: 'none' },
                        overflow: { xs: 'auto', sm: 'visible' }
                    }}
                >
                    <Table size={isMobile ? "small" : "medium"}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Seller</TableCell>
                                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Business</TableCell>
                                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Contact</TableCell>
                                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Products</TableCell>
                                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Applied</TableCell>
                                <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sellers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((seller) => (
                                <TableRow key={seller._id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Avatar
                                                src={seller.profileImage}
                                                sx={{ bgcolor: '#FF9800', width: isMobile ? 40 : 48, height: isMobile ? 40 : 48 }}
                                            >
                                                <Store sx={{ fontSize: isMobile ? 16 : 20 }} />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                                                    {seller.brandName || seller.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                                                    {seller.email}
                                                </Typography>
                                                {/* Mobile info display */}
                                                <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Applied: {seller.createdAt ? new Date(seller.createdAt).toLocaleDateString() : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                                            {seller.storeDescription || seller.businessDescription || 'No description'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {seller.phone && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Phone fontSize="small" color="action" />
                                                    <Typography variant="caption">{seller.phone}</Typography>
                                                </Box>
                                            )}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Email fontSize="small" color="action" />
                                                <Typography variant="caption">{seller.email}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Chip
                                            label={seller.stats?.totalProducts || 0}
                                            color="primary"
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                        <Typography variant="body2">
                                            {seller.createdAt ? new Date(seller.createdAt).toLocaleDateString() : 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    onClick={() => handleViewDetails(seller)}
                                                    color="primary"
                                                    size={isMobile ? "small" : "medium"}
                                                >
                                                    <Visibility sx={{ fontSize: isMobile ? 16 : 20 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Approve">
                                                <IconButton
                                                    onClick={() => {
                                                        setSelectedSeller(seller);
                                                        setApproveDialog(true);
                                                    }}
                                                    color="success"
                                                    size={isMobile ? "small" : "medium"}
                                                >
                                                    <CheckCircle sx={{ fontSize: isMobile ? 16 : 20 }} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Reject">
                                                <IconButton
                                                    onClick={() => {
                                                        setSelectedSeller(seller);
                                                        setRejectDialog(true);
                                                    }}
                                                    color="error"
                                                    size={isMobile ? "small" : "medium"}
                                                >
                                                    <Close sx={{ fontSize: isMobile ? 16 : 20 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Pagination */}
            {sellers.length > rowsPerPage && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={sellers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            )}

            {/* Seller Details Dialog */}
            <Dialog 
                open={detailsDialog} 
                onClose={() => setDetailsDialog(false)} 
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#FF9800', width: 48, height: 48 }}>
                            <Store />
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                {selectedSeller?.brandName || selectedSeller?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedSeller?.email}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedSeller && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Business Name
                                </Typography>
                                <Typography variant="body1" fontWeight={600}>
                                    {selectedSeller.brandName || selectedSeller.name}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Contact Number
                                </Typography>
                                <Typography variant="body1">
                                    {selectedSeller.phone || 'Not provided'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Business Description
                                </Typography>
                                <Typography variant="body1">
                                    {selectedSeller.storeDescription || selectedSeller.businessDescription || 'No description provided'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Applied Date
                                </Typography>
                                <Typography variant="body1">
                                    {selectedSeller.createdAt ? new Date(selectedSeller.createdAt).toLocaleDateString() : 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Products Submitted
                                </Typography>
                                <Typography variant="body1">
                                    {selectedSeller.stats?.totalProducts || 0} products
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetailsDialog(false)}>Close</Button>
                    <Button 
                        onClick={() => {
                            setDetailsDialog(false);
                            setApproveDialog(true);
                        }}
                        variant="contained" 
                        color="success"
                    >
                        Approve
                    </Button>
                    <Button 
                        onClick={() => {
                            setDetailsDialog(false);
                            setRejectDialog(true);
                        }}
                        variant="contained" 
                        color="error"
                    >
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Approve Dialog */}
            <Dialog open={approveDialog} onClose={() => setApproveDialog(false)}>
                <DialogTitle>Approve Seller</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to approve <strong>{selectedSeller?.brandName || selectedSeller?.name}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This will allow them to start selling on the platform.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setApproveDialog(false)} disabled={processing}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleApproveSeller} 
                        variant="contained" 
                        color="success"
                        disabled={processing}
                    >
                        {processing ? <CircularProgress size={20} /> : 'Approve'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)}>
                <DialogTitle>Reject Seller</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to reject <strong>{selectedSeller?.brandName || selectedSeller?.name}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This will prevent them from selling on the platform. They can reapply later.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectDialog(false)} disabled={processing}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleRejectSeller} 
                        variant="contained" 
                        color="error"
                        disabled={processing}
                    >
                        {processing ? <CircularProgress size={20} /> : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PendingSellers;
